import { ChevronsUpDownIcon } from "lucide-react-native";
import React, { memo } from "react";
import { NativeScrollEvent, NativeSyntheticEvent } from "react-native";
import { RenderItem } from "react-native-draggable-flatlist";

import MultiSelectSearchInput from "@/components/molecules/MultiSelectSearchInput";
import SkeletonPlaceholder from "@/components/molecules/SkeletonPlaceholder";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";

import { IFilterOption } from "@/stores/filterValues";

/**
 * Props for the CustomMultiSelectView component
 */
interface Props<T extends IFilterOption> {
  readonly data: readonly T[];
  readonly listSelected: readonly T[];
  readonly isOpenDropdown: boolean;
  readonly renderListSelected: (item: T, index: number) => React.JSX.Element;
  readonly onToggleDropdown: () => void;
  readonly renderDropdownItem: RenderItem<T>;
  readonly renderEmpty: () => React.JSX.Element;
  readonly onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  readonly renderFooterComponent?: () => React.JSX.Element;
  readonly title: string;
  readonly loading: boolean;
  readonly isDark: boolean;
  /** Whether to render a type-to-filter search input above the option list. */
  readonly enableSearch?: boolean;
  /** Current search text (controlled by the container) when enableSearch is true. */
  readonly searchText?: string;
  /** Placeholder for the search input when enableSearch is true. */
  readonly searchPlaceholder?: string;
  /** Handler invoked as the user types in the search input. */
  readonly onSearchTextChange?: (value: string) => void;
}

/** Scroll event throttle in milliseconds */
const SCROLL_EVENT_THROTTLE = 400;
/** No-op drag handler for the draggable-flatlist RenderItem contract. */
const noopDrag = () => {};

/**
 * Renders a multi-select dropdown with selected items display.
 * Uses a Pressable trigger instead of Accordion to avoid AnimatedHeight's
 * overflow:hidden which clips nested ScrollViews and prevents scrolling.
 * @param root0 - Component props
 * @param root0.data - Array of available options to display in dropdown
 * @param root0.loading - Whether initial data is loading (shows skeleton placeholder)
 * @param root0.title - Title text displayed above dropdown and in "Select {title}" placeholder
 * @param root0.listSelected - Array of currently selected items to render as chips
 * @param root0.isOpenDropdown - Whether the dropdown is expanded
 * @param root0.onToggleDropdown - Callback to toggle dropdown open/closed state
 * @param root0.renderListSelected - Function to render each selected item as a chip
 * @param root0.renderDropdownItem - Function to render each item in the dropdown list
 * @param root0.renderEmpty - Function to render empty state when no options available
 * @param root0.onScroll - Callback invoked on scroll events for detecting when user is near end
 * @param root0.renderFooterComponent - Function to render footer component (typically loading indicator)
 * @param root0.isDark - Whether dark theme is active (affects background colors)
 * @param root0.enableSearch - Whether to render a type-to-filter search input above the option list
 * @param root0.searchText - Current search text (controlled by the container)
 * @param root0.searchPlaceholder - Placeholder for the search input
 * @param root0.onSearchTextChange - Handler invoked as the user types in the search input
 * @returns The multi-select view
 */
const CustomMultiSelectView = <T extends IFilterOption>({
  data,
  loading,
  title,
  listSelected,
  isOpenDropdown,
  onToggleDropdown,
  renderListSelected,
  renderDropdownItem,
  renderEmpty,
  onScroll,
  renderFooterComponent,
  isDark,
  enableSearch,
  searchText,
  searchPlaceholder,
  onSearchTextChange,
}: Props<T>) => (
  <Stack className="gap-1">
    <Heading className="font-body text-body uppercase" variant="title-sm">
      {title}
    </Heading>
    {loading ? (
      <SkeletonPlaceholder>
        <Box className="h-12 w-full rounded-sm bg-surface-strong" />
      </SkeletonPlaceholder>
    ) : (
      <Stack className="gap-3">
        <Pressable
          onPress={onToggleDropdown}
          className={`h-12 flex-row items-center justify-between rounded-md px-3 ${isDark ? "bg-background-gray" : "bg-surface-muted"}`}
        >
          <Text
            variant="unstyled"
            className={`font-body text-title ${isDark ? "text-white" : "text-content-primary"}`}
          >
            Select {title}
          </Text>
          <Icon
            as={ChevronsUpDownIcon}
            size="xl"
            className={
              isDark ? "text-prompt-buttonText" : "text-content-primary"
            }
          />
        </Pressable>
        {!!listSelected.length && !isOpenDropdown && (
          <Stack direction="horizontal" className="flex-wrap gap-2">
            {listSelected.map((item, index) => renderListSelected(item, index))}
          </Stack>
        )}
        {isOpenDropdown && (
          <Box
            className={`rounded-sm ${isDark ? "bg-background-gray" : "bg-surface-muted"}`}
          >
            {enableSearch && (
              <MultiSelectSearchInput
                searchText={searchText}
                searchPlaceholder={searchPlaceholder}
                title={title}
                onSearchTextChange={onSearchTextChange}
              />
            )}
            <ScrollView
              className="max-h-80"
              nestedScrollEnabled
              onScroll={onScroll}
              scrollEventThrottle={SCROLL_EVENT_THROTTLE}
            >
              <Box
                className={isDark ? "bg-background-gray" : "bg-surface-muted"}
              >
                {data.length === 0
                  ? renderEmpty()
                  : data.map((item, index) =>
                      renderDropdownItem({
                        item,
                        drag: noopDrag,
                        isActive: false,
                        getIndex: () => index,
                      })
                    )}
                {renderFooterComponent?.()}
              </Box>
            </ScrollView>
          </Box>
        )}
      </Stack>
    )}
  </Stack>
);

CustomMultiSelectView.displayName = "CustomMultiSelectView";

// eslint-disable-next-line component-structure/require-memo-in-view -- Type assertion needed for generic component
export default memo(CustomMultiSelectView) as typeof CustomMultiSelectView;
